export type Slot<Organic, Sponsored> =
  | { kind: 'organic'; data: Organic; position: number }
  | { kind: 'sponsored'; data: Sponsored; position: number };

/**
 * Interleaves an organic stream with a sponsored stream. Sponsored items take
 * the 1-indexed slots listed in `sponsoredPositions`; organic items fill the
 * remaining slots in their original order.
 *
 * Each Slot's `position` is the resulting render index (used for stable React
 * keys + a11y) — it is NOT supplied by either input array. The arrays
 * themselves are consumed in order; sponsored items are not reordered to
 * match `sponsoredPositions`. Positions past the end of both streams are
 * dropped.
 */
export function interleave<O, S>(
  organic: O[],
  sponsored: S[],
  sponsoredPositions: number[],
): Slot<O, S>[] {
  const out: Slot<O, S>[] = [];
  const sq = [...sponsored];
  const oq = [...organic];
  const positions = new Set(sponsoredPositions);
  const total = organic.length + Math.min(sponsored.length, positions.size);

  for (let pos = 1; pos <= total; pos++) {
    if (positions.has(pos) && sq.length > 0) {
      out.push({ kind: 'sponsored', data: sq.shift()!, position: pos });
    } else if (oq.length > 0) {
      out.push({ kind: 'organic', data: oq.shift()!, position: pos });
    }
  }
  return out;
}

/** Parse "1,5,10" into [1, 5, 10]. Ignores NaN and non-positive entries. */
export function parseSponsoredPositions(raw?: string): number[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}
